import React, { createContext, useEffect, useState, useRef } from 'react';
import useFetchData from '../hooks/useFetchData';
import { set } from 'lodash';
import { searchUtils } from '../utils/search.utils';
import { getCorrectPageCount } from '../utils/table.utils';
import { formatConfigurationForSelection } from '../utils/format.utils';
import { createDefaultConfiguration, formatDefaultConfigurationToAddCapsule } from '../utils/capsule_configuration.utils';
import { useOutletContext } from 'react-router-dom';

const CapsuleConfiguratorContext = createContext();

const CapsuleConfiguratorProvider = ({ defaultCapsule, defaultConfig, selectedConfigurationId, children }) => {
    // Ajout d'une référence pour suivre si le composant a été monté
    const isMounted = useRef(false);
    const { setData: updateCustomerData, customer } = useOutletContext();
    /**
     * STATE FOR DILUTION
     */
    const { data: dilutions, error: dilutionApiError, isLoading: isDilutionLoading } = useFetchData('/dilutions');
    /**
     * STATE FOR CAPSULE CONFIGURATION
     */
    const hasDefaultConfig = (defaultConfig && Object.keys(defaultConfig).length > 0 && defaultCapsule) ? true : false;
    const [capsuleConfiguration, setCapsuleConfiguration] = useState(() =>
        hasDefaultConfig
            ? formatDefaultConfigurationToAddCapsule(defaultConfig, defaultCapsule.name, defaultCapsule.id)
            : createDefaultConfiguration()
    );

    // Utiliser une fonction d'initialisation pour éviter le calcul pendant le rendu
    const [showList, setShowList] = useState(false);
    const [configurationSelection, setConfigurationSelection] = useState({});
    const [countByCapsule, setCountByCapsule] = useState({});
    const contentConfiguration = capsuleConfiguration?.content || [];

    /*
     * STATE FOR MIXTURES TABLE
     */
    const [API_URL, setAPI_URL] = useState('/mixtures/physiques');
    const [pages, setPages] = useState([1]);
    const [fetchAll, setFetchAll] = useState(true);
    const [mixturesTableData, setMixturesTableData] = useState(null);
    const { data: mixturesData, error: mixturesApiError, isLoading: isMixturesLoading } = useFetchData(API_URL, fetchAll ? null : pages[0]);
    const [searchType, setSearchType] = React.useState(searchUtils.SEARCH_TYPES.exact);

    const [pagination, setPagination] = React.useState({ pageIndex: 0, pageSize: 50 });
    const [pageCount, setPageCount] = React.useState(1);
    const [sorting, setSorting] = useState([]);

    // Initialiser totalItemsFetched avec 0 au lieu de mixturesData?.values.length
    const [totalItemsFetched, setTotalItemsFetched] = useState(0);
    const [globalFilter, setGlobalFilter] = useState({ keys: [], value: '' });

    // Initialiser mixturesSelection avec un objet vide, et le mettre à jour dans un useEffect
    const [mixturesSelection, setMixturesSelection] = useState({});
    const [tableContext, setTableContext] = useState(null);

    // Ajouter l'état pour le catalogue sélectionné
    const [selectedCatalogue, setSelectedCatalogue] = useState('physiques');

    // Initialiser mixturesSelection après le premier rendu
    useEffect(() => {
        if (contentConfiguration.length > 0) {
            setMixturesSelection(formatConfigurationForSelection(contentConfiguration) || {});
        }
    }, []); // Dépendance vide pour ne s'exécuter qu'une fois

    const handleCatalogueFetch = () => {
        setAPI_URL('/mixtures/catalogue');
        setSelectedCatalogue('tous');
    };

    const handleCustomMixturesFetch = () => {
        setAPI_URL('/mixtures/custom');
        setSelectedCatalogue('custom');
    };

    const handlePhysiquesMixtureFetch = () => {
        setAPI_URL('/mixtures/physiques');
        setSelectedCatalogue('physiques');
    };

    const handleEffluencesMixturesFetch = () => {
        setAPI_URL('/mixtures/effluences');
        setSelectedCatalogue('effluences');
    };

    const handleOthersMixturesFetch = () => {
        setAPI_URL('/mixtures/others');
        setSelectedCatalogue('autres');
    };

    const updateConfigurationContent = (newContent) => {
        setCapsuleConfiguration((prev) => {
            return {
                ...prev,
                content: newContent
            };
        });
        updateCustomerData((prev) => {
            return {
                ...prev,
                capsules: prev.capsules.map((capsule) => {
                    if (capsule.id === defaultCapsule.id) {
                        return {
                            ...capsule,
                            configurations: capsule.configurations.map((config) => {
                                if (config.id === selectedConfigurationId) {
                                    return {
                                        ...config,
                                        configuration: { ...config.configuration, content: newContent }
                                    };
                                }
                                return config;
                            })
                        };
                    }
                    return capsule;
                })
            };
        });
    };

    const updateConfigurationIsValid = (isValid) => {
        updateCustomerData((prev) => {
            return {
                ...prev,
                capsules: prev.capsules.map((capsule) => {
                    if (capsule.id === defaultCapsule.id) {
                        return {
                            ...capsule,
                            configurations: capsule.configurations.map((config) => {
                                if (config.id === selectedConfigurationId) {
                                    return {
                                        ...config,
                                        isValid: isValid
                                    };
                                }
                                return config;
                            })
                        };
                    }
                    return capsule;
                })
            };
        });
    }

    const resetBeforeFetch = () => {
        setPagination(prevPagination => ({
            ...prevPagination,
            pageIndex: 0
        }));
        setTotalItemsFetched(0);
        setPages([1]);
        setMixturesSelection(formatConfigurationForSelection(contentConfiguration) || {});
        setMixturesTableData(null);
    };

    const synchronizeConfigurations = (updatedConfigurations) => {

        const capsuleId = updatedConfigurations[0].capsuleId;
        const configurationIds = updatedConfigurations.map((config) => config.id);

        updateCustomerData((prev) => {
            return {
                ...prev,
                capsules: prev.capsules.map((capsule) => {
                    if (capsule.id === capsuleId) {
                        return {
                            ...capsule,
                            configurations: capsule.configurations.map((config) => {
                                if (configurationIds.includes(config.id)) {
                                    const updatedConfig = updatedConfigurations.find((updatedConfig) => updatedConfig.id === config.id).configuration;
                                    console.log(updatedConfig)
                                    return {
                                        ...config,
                                        configuration: { ...updatedConfig }
                                    };
                                }
                                return config;
                            })
                        };
                    }
                    return capsule;
                })
            };
        });
    };


    const handleMixturesChanges = () => {
        if (!mixturesData) return; // Vérification de sécurité supplémentaire

        setMixturesTableData((prev) => {
            return {
                pagination: mixturesData.pagination,
                values: !mixturesTableData || fetchAll ? mixturesData?.values : [...prev?.values, ...mixturesData?.values]
            };
        });

        const correctPageCount = getCorrectPageCount(mixturesData.pagination.total, pagination.pageSize);
        const pageIndex = !mixturesTableData ? 0 : pagination.pageIndex;
        const totalItems = !mixturesTableData || fetchAll ? mixturesData.values.length : totalItemsFetched + mixturesData.values.length;

        setPagination({ pageIndex, pageSize: mixturesData.pagination.pageCount > 1 ? pagination.pageSize : 50 });
        setPageCount(correctPageCount > 0 ? correctPageCount : 1);
        setTotalItemsFetched(totalItems);
    };

    // Indiquer que le composant est monté
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        resetBeforeFetch();
    }, [API_URL]);

    // Utiliser la référence isMounted pour éviter les mises à jour après démontage
    useEffect(() => {
        // Éviter cette mise à jour pendant le rendu initial
        if (!isMounted.current) return;

        const newConfig = defaultCapsule?.configurations?.find(config => config.id === selectedConfigurationId);
        if (newConfig) {
            // Utiliser setTimeout pour retarder les mises à jour d'état
            setTimeout(() => {
                if (isMounted.current) {
                    setCapsuleConfiguration(formatDefaultConfigurationToAddCapsule(
                        newConfig.configuration,
                        defaultCapsule?.name,
                        defaultCapsule?.id
                    ));
                    setMixturesSelection(formatConfigurationForSelection(newConfig.configuration.content) || {});
                }
            }, 0);
        }
    }, [selectedConfigurationId, defaultCapsule?.id]);

    useEffect(() => {
        if (selectedConfigurationId) {
            setConfigurationSelection({});
        }
    }, [selectedConfigurationId]);

    useEffect(() => {
        if (!isMixturesLoading && mixturesData !== null) {
            handleMixturesChanges();
        }
    }, [mixturesData, isMixturesLoading]);

    useEffect(() => {
        if (globalFilter.value.length === 0) {
            setPagination(prevPagination => ({
                ...prevPagination,
                pageIndex: 0
            }));
        }
    }, [globalFilter.value]);

    const contextValue = {
        capsuleConfiguration,
        setCapsuleConfiguration,
        contentConfiguration,
        updateConfigurationContent,
        showList,
        setShowList,
        mixturesTableData,
        setMixturesTableData,
        pages,
        setPages,
        fetchAll,
        setFetchAll,
        setAPI_URL,
        API_URL,
        isMixturesLoading,
        searchType,
        setSearchType,
        pagination,
        setPagination,
        pageCount,
        setPageCount,
        sorting,
        setSorting,
        totalItemsFetched,
        setTotalItemsFetched,
        globalFilter,
        setGlobalFilter,
        mixturesSelection,
        setMixturesSelection,
        configurationSelection,
        setConfigurationSelection,
        selectedConfigurationId,
        dilutions,
        selectedCatalogue,
        setSelectedCatalogue,
        countByCapsule,
        setCountByCapsule,
        defaultCapsule,
        handleCatalogueFetch,
        handleCustomMixturesFetch,
        handlePhysiquesMixtureFetch,
        handleEffluencesMixturesFetch,
        handleOthersMixturesFetch,
        updateConfigurationIsValid,
        synchronizeConfigurations
    };
    return (
        <CapsuleConfiguratorContext.Provider value={contextValue}>
            {children}
        </CapsuleConfiguratorContext.Provider>
    );
};

const useCapsuleConfigurator = () => {
    const context = React.useContext(CapsuleConfiguratorContext);
    if (context === undefined) {
        throw new Error('useCapsuleConfigurator must be used within a CapsuleConfiguratorProvider');
    }
    if (!context.capsuleConfiguration || !context.setCapsuleConfiguration) {
        throw new Error('CapsuleConfiguratorProvider must have a defaultConfig and children');
    }
    return context;
};

export { useCapsuleConfigurator, CapsuleConfiguratorProvider };
